import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';

/**
 * Generate a branded PDF report.
 *
 * @param {Object} opts
 * @param {string} opts.title       - Big report title (e.g. "Orders Report")
 * @param {string} [opts.subtitle]  - Small subtitle under the title
 * @param {Array<{label: string, value: string|number}>} [opts.summary]
 *                                  - Key-value summary cards
 * @param {Array<{title: string, head: string[], rows: Array<Array>}>} [opts.tables]
 *                                  - One or more tables rendered sequentially
 * @param {string} [opts.filename]  - Output filename (default derived from title)
 */
export function downloadPdfReport({ title, subtitle = '', summary = [], tables = [], filename }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const leftMargin = 40;
  let cursorY = 40;

  // Brand header bar
  doc.setFillColor(245, 158, 11); // amber-500
  doc.rect(0, 0, pageWidth, 6, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(20);
  doc.text('Evabrew Roastery Management', leftMargin, cursorY);
  cursorY += 22;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(60, 60, 60);
  doc.text(title, leftMargin, cursorY);
  cursorY += 16;

  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(110, 110, 110);
    doc.text(subtitle, leftMargin, cursorY);
    cursorY += 14;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  doc.text(`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`, leftMargin, cursorY);
  cursorY += 18;

  // Summary strip (up to 4 cards per row)
  if (summary.length > 0) {
    const cardsPerRow = 4;
    const gap = 12;
    const cardW = (pageWidth - leftMargin * 2 - gap * (cardsPerRow - 1)) / cardsPerRow;
    const cardH = 46;
    summary.forEach((s, i) => {
      const col = i % cardsPerRow;
      const row = Math.floor(i / cardsPerRow);
      const x = leftMargin + col * (cardW + gap);
      const y = cursorY + row * (cardH + gap);
      doc.setDrawColor(230, 230, 230);
      doc.setFillColor(252, 252, 252);
      doc.roundedRect(x, y, cardW, cardH, 4, 4, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(String(s.label || '').toUpperCase(), x + 10, y + 14);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(30, 30, 30);
      doc.text(String(s.value ?? '-'), x + 10, y + 32);
    });
    const rows = Math.ceil(summary.length / cardsPerRow);
    cursorY += rows * (cardH + gap) + 4;
  }

  // Tables
  tables.forEach((t, idx) => {
    if (idx > 0) cursorY += 10;
    if (t.title) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(60, 60, 60);
      doc.text(t.title, leftMargin, cursorY);
      cursorY += 8;
    }
    autoTable(doc, {
      head: [t.head],
      body: t.rows,
      startY: cursorY,
      margin: { left: leftMargin, right: leftMargin },
      styles: { fontSize: 9, cellPadding: 5, overflow: 'linebreak' },
      headStyles: { fillColor: [245, 158, 11], textColor: 255, halign: 'left' },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      didDrawPage: (data) => {
        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth - leftMargin,
          doc.internal.pageSize.getHeight() - 16,
          { align: 'right' }
        );
      },
    });
    cursorY = doc.lastAutoTable.finalY + 10;
  });

  const fname =
    filename ||
    `${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
  doc.save(fname);
}
